import { useEffect, useRef, useState } from 'react'
import './App.css'
import { BusinessMetrics } from './components/BusinessMetrics'
import { InfoTip } from './components/InfoTip'
import { ask, stoppedMessage } from './api/ask'
import { apiFetch } from './api/http'
import {
  clearDashboardMemory, clearLegacyDashboardBackup, dashboardBackup, fetchDashboards,
  hasPendingDashboards, legacyDashboardBackup, stageDashboard, stageDashboardDeletion, syncDashboards,
} from './api/dashboards'
import { clearRunSpecCache, OutOfScopeError, runSpec } from './api/runSpec'
import { OUT_OF_SCOPE_CONTRACT, withPreviewOutOfScope } from './cardScope'
import { isAnswer } from './api/contracts'
import { neutralLegacyText } from './legacySourceText'
import type {
  Answer,
  AssistantTurn,
  CardLayout,
  ChartCard,
  ChatTurn,
  Dashboard,
  DashboardCard,
  ActionCard,
  ActionItem,
  HistoryTurn,
  TextCard,
} from './types'
import { Menu } from 'lucide-react'
import { ChatView } from './components/ChatView'
import { AskBar } from './components/AskBar'
import { Sidebar } from './components/Sidebar'
import { SupplierPicker } from './components/SupplierPicker'
import { AdminUsers } from './components/AdminUsers'
import { ACTIVE_SUPPLIER, clearClientState, setSupplier, syncSupplier } from './tenant'
import { AreaTopLists } from './components/AreaTopLists'
import { DashboardView } from './components/DashboardView'
import { DashboardIndex } from './components/DashboardIndex'
import type { SectionId } from './sections'
import type { Insight } from './insights'
import { DataFreshnessNote } from './components/DataFreshnessNote'
import { LandingHero } from './components/LandingHero'
import { Login } from './components/Login'
import { ChangePassword } from './components/ChangePassword'
import { MonthPicker } from './components/MonthPicker'
import { AreaPackages } from './components/AreaPackages'
import { SegmentView } from './components/SegmentView'
import type { ShareCardFigure } from './mainSegmentShare'
import { logout, me, UNAUTHORIZED_EVENT, type AuthUser } from './api/auth'
import { type ApiStatus } from './health'
import { fetchPeriods, readStoredPeriod, resolveInitialPeriod, storePeriod, STG_LANDING_ID, type Period } from './landing'
import { USER_MGMT_UI_ENABLED } from './flags'
import { chatAvailable, previewingSupplier } from './chatAccess'
import { ReportingPeriodsState } from './components/ReportingPeriodsState'
import { NameDialog } from './components/NameDialog'
import { usePopoverDismiss } from './usePopoverDismiss'
import { DATA_SOURCE_NAME } from './missingLabels'
import {
  clearReturnPath, DEFAULT_ROUTE, parsePath, pathFor, rememberReturnPath, takeReturnPath, useRoute,
} from './routes'

const uuid = () => crypto.randomUUID()

const nowIso = () => new Date().toISOString()

function summarize(q: string): string {
  const s = q.trim().replace(/\?+$/, '')
  if (!s) return 'Pin answer'
  const titled = s.charAt(0).toUpperCase() + s.slice(1)
  return titled.length > 60 ? `${titled.slice(0, 57)}…` : titled
}

// Older stored cards predate the type discriminator; treat them as charts.
function normalize(dashboards: Dashboard[]): Dashboard[] {
  return dashboards.filter((d) => d && typeof d.id === 'string').map((d) => ({
    ...d,
    name: typeof d.name === 'string' ? d.name : 'Dashboard',
    cards: (Array.isArray(d.cards) ? d.cards : []).filter((c) => c && typeof c.id === 'string').map((c) => {
      const typed = ((c as { type?: string }).type ? c : { ...(c as object), type: 'chart' }) as DashboardCard
      if (typed.type === 'text') return { ...typed, heading: typed.heading ?? '', body: typed.body ?? '' }
      if (typed.type === 'action') return { ...typed, heading: typed.heading ?? '',
        items: (Array.isArray(typed.items) ? typed.items : []).filter((it: ActionItem) => it && typeof it.id === 'string' && typeof it.text === 'string') }
      const chart = typed as ChartCard
      // MPD-84 N1: an out-of-scope card never shows stored figures, whatever the saved copy holds.
      if (chart.out_of_scope === true) return { ...chart, type: 'chart' as const, title: chart.title ?? 'Saved insight',
        spec: chart.spec ?? null, last_contract: OUT_OF_SCOPE_CONTRACT }
      return { ...chart, type: 'chart' as const, title: chart.title ?? 'Saved insight', spec: chart.spec ?? null,
        // MPD-118: an answer saved with the old source wording (server, browser backup or the legacy
        // local board) reads neutral, also when the card never refreshes or its refresh fails.
        last_contract: isAnswer(chart.last_contract) ? neutralLegacyText(chart.last_contract)
          : { answer: 'This saved answer could not be read. Update the card or re-run its question to recover it.', caveat: null, chart: null } }
    }),
  }))
}

export default function App() {
  // undefined = still checking the session; null = not logged in; user = authed.
  const [authUser, setAuthUser] = useState<AuthUser | null | undefined>(undefined)
  const [authError, setAuthError] = useState(false)
  const [authAttempt, setAuthAttempt] = useState(0)
  const [status, setStatus] = useState<ApiStatus | null>(null)
  // Shared month for the Marknad section (segment view + market-share card). Absent this,
  // both were pinned to the latest complete month and could never show an earlier one.
  const [periods, setPeriods] = useState<Period[]>([])
  const [periodsLoading, setPeriodsLoading] = useState(true)
  const [periodsReload, setPeriodsReload] = useState(0)
  // A4/C15 — THE period for the whole app. Every view below reads this one; it used to live
  // in three independent places, so two views could show different months at once and a
  // reload reset the choice.
  const [period, setPeriod] = useState<Period | null>(null)
  const choosePeriod = (p: Period) => {
    setPeriod(p)
    storePeriod(p.value)
  }
  const [turns, setTurns] = useState<ChatTurn[]>([])
  const [value, setValue] = useState('')
  const [busy, setBusy] = useState(false)

  const [dashboards, setDashboards] = useState<Dashboard[]>([])
  const [syncStatus, setSyncStatus] = useState<'loading' | 'saving' | 'saved' | 'error'>('loading')
  const [hasLegacyBoards, setHasLegacyBoards] = useState(false)
  const [cardErrors, setCardErrors] = useState<Record<string, string>>({})
  const [workspaceError, setWorkspaceError] = useState<string | null>(null)
  const [logoutError, setLogoutError] = useState<string | null>(null)
  const loggingOut = useRef(false)
  // The fixed backbone is the active section. Within 'mina' (the dashboard
  // workspace), activeId === null shows the AI chat (the build tool); a board id
  // shows that board. The chat lives ONLY here — it's a feature of Mina dashboards.
  // MP-OS-189 — both come from the URL (routes.ts), so Back/Forward, reloads (a supplier
  // switch reloads to re-resolve TENANT) and shared links all land on the same view.
  // MP-OS-193: no chat for suppliers or an admin's supplier preview. Routing reads the same
  // rule, so /chat (link, Back/Forward, remembered path) resolves to the board list for them.
  const chatOn = chatAvailable(authUser, previewingSupplier())
  const { route, navigate } = useRoute(chatOn)
  const activeSection: SectionId = route.section
  const activeId = route.section === 'mina' ? route.boardId : null
  const openMyDashboards = (options?: { replace?: boolean }) => navigate({ section: 'mina', boardId: null }, options)
  const openBoard = (id: string, options?: { replace?: boolean }) => navigate({ section: 'mina', boardId: id }, options)
  // A board's saved queries re-run once each time it is opened (click, link, Back/Forward).
  const openedBoard = useRef<string | null>(null)
  // New-dashboard naming: open = the dialog is up; `turn` = the chat answer to pin into it.
  const [naming, setNaming] = useState<{ turn?: AssistantTurn } | null>(null)
  const [askTargetId, setAskTargetId] = useState<string | null>(null)
  const [collapsed, setCollapsed] = useState(false)
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  // The phone nav drawer is a menu like any other: Escape closes it and focus returns to its
  // hamburger (shared popover behaviour, MP-OS-188 bug #4).
  const mobileNavToggleRef = useRef<HTMLButtonElement>(null)
  const mobileNavRef = useRef<HTMLDivElement>(null)
  usePopoverDismiss({ open: mobileNavOpen, close: () => setMobileNavOpen(false), triggerRef: mobileNavToggleRef, containerRef: mobileNavRef })
  const [pinnedTurnIds, setPinnedTurnIds] = useState<Set<string>>(new Set())
  const [refreshingIds, setRefreshingIds] = useState<Set<string>>(new Set())
  // MPD-91 L1: cards refused under an admin's supplier preview. View state only, never saved.
  const [previewOutOfScope, setPreviewOutOfScope] = useState<ReadonlySet<string>>(new Set())
  const setPreviewRefused = (cardId: string, refused: boolean) => setPreviewOutOfScope((prev) => {
    if (prev.has(cardId) === refused) return prev
    const next = new Set(prev)
    if (refused) next.add(cardId); else next.delete(cardId)
    return next
  })
  const [draggingTurn, setDraggingTurn] = useState<AssistantTurn | null>(null)

  const history = useRef<HistoryTurn[]>([])
  // Owner of the last usable session this tab saw signed in, for tagging a path parked by a 401
  // (authUser is already null by the time that park runs). null = no session seen yet.
  const lastUsernameRef = useRef<string | null>(null)
  // Set just before an explicit sign-out clears authUser, so the deep-link effect below does not
  // re-park the page the sign-out itself navigates to (e.g. the default route).
  const signedOutRef = useRef(false)
  const abort = useRef<AbortController | null>(null)
  const saveTimer = useRef<number | null>(null)
  const dashboardSession = useRef<AbortController | null>(null)
  const dashboardsRef = useRef<Dashboard[]>([])
  const boardRevision = useRef(0)
  const boardEdits = useRef(new Map<string, number>())
  const refreshInFlight = useRef(new Set<string>())

  const replaceDashboards = (next: Dashboard[]) => {
    dashboardsRef.current = next
    setDashboards(next)
  }

  // Deep links across sign-in: park the requested page while signed out (SSO comes back to the
  // site root), restore it once there is a usable session.
  // MP-OS-189 follow-up: tag the parked path with the account that was signed in when it was
  // parked (or null, before any sign-in). lastUsernameRef holds that owner — it is only ever
  // updated while a usable session exists, so a 401 (which clears authUser to null) still finds
  // the account it belonged to when this effect next runs.
  useEffect(() => {
    if (authUser === null) {
      if (signedOutRef.current) signedOutRef.current = false
      else rememberReturnPath(lastUsernameRef.current)
      return
    }
    if (!authUser || authUser.must_change) return
    lastUsernameRef.current = authUser.username
    const back = takeReturnPath(authUser.username)
    const target = back ? parsePath(back) : null
    if (target && window.location.pathname === '/') navigate(target, { replace: true })
  }, [authUser, navigate])

  // State normalisation (replace, never push): aliases like `/` and `/dashboards`, unknown
  // paths and any query string become the canonical URL of the view actually shown. Only with
  // a session — signed out, the URL is left alone so sign-in can return to it.
  useEffect(() => {
    if (!authUser || authUser.must_change) return
    let target = parsePath(window.location.pathname) ?? DEFAULT_ROUTE
    if (target.section === 'admin' && !USER_MGMT_UI_ENABLED) target = DEFAULT_ROUTE
    const here = window.location.pathname + window.location.search + window.location.hash
    if (here !== pathFor(target, chatOn)) navigate(target, { replace: true })
  }, [authUser, route, navigate, chatOn])

  useEffect(() => {
    let cancelled = false
    setAuthError(false)
    void me().then((user) => { if (!cancelled) setAuthUser(user) })
      .catch(() => { if (!cancelled) setAuthError(true) })
    return () => { cancelled = true }
  }, [authAttempt])

  // Periods must be fetched WITH a session, and re-fetched once one exists — otherwise the
  // very first login (no prior session) fetches before the cookie is set, gets nothing, and
  // never retries, so the dashboard stays empty until a manual reload (Olof: "man måste ladda
  // om sidan"). Gating on authUser makes login itself load the data.
  useEffect(() => {
    if (!authUser || authUser.must_change) return
    let cancelled = false
    setPeriodsLoading(true)
    setPeriods([])
    setPeriod(null)
    void fetchPeriods().then((ps) => {
      if (cancelled) return
      setPeriods(ps)
      setPeriod(resolveInitialPeriod(ps, readStoredPeriod()))
      setPeriodsLoading(false)
    }).catch(() => {
      if (!cancelled) setPeriodsLoading(false)
    })
    return () => { cancelled = true }
  }, [authUser, periodsReload])

  // A supplier seat is locked to its own company: force the active supplier to the
  // login's manufacturer (persist + reload re-scopes TENANT). Admins (Olof) keep the
  // free picker and see everyone.
  useEffect(() => {
    if (authUser?.role === 'supplier' && !authUser.must_change && authUser.manufacturer) {
      syncSupplier(authUser.manufacturer, authUser.manufacturer_label)
    }
  }, [authUser])

  // A 401 from any API call mid-session (expired session) → drop to the login screen
  // instead of leaving a dashboard full of "kunde inte hämta"-errors.
  useEffect(() => {
    const onLost = () => {
      abort.current?.abort()
      dashboardSession.current?.abort()
      // An expired session is not an explicit discard: retain account-scoped
      // unsent edits for re-login, while clearing all displayed/cross-account state.
      clearClientState({ preserveDashboardBackups: true })
      clearDashboardMemory()
      clearRunSpecCache()
      replaceDashboards([])
      setTurns([])
      history.current = []
      setAskTargetId(null)
      setNaming(null)
      setCardErrors({})
      setWorkspaceError(null)
      setBusy(false)
      setAuthUser(null)
    }
    window.addEventListener(UNAUTHORIZED_EVENT, onLost)
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onLost)
  }, [])

  useEffect(() => {
    if (!authUser || authUser.must_change) return // wait for a usable, non-temporary session
    const controller = new AbortController()
    dashboardSession.current = controller
    setStatus(null)
    replaceDashboards(normalize(dashboardBackup(authUser.username)).filter((b) => b.id !== STG_LANDING_ID))
    setHasLegacyBoards(legacyDashboardBackup().some((b) => b.id !== STG_LANDING_ID))
    apiFetch(`${import.meta.env.VITE_API_URL ?? ''}/api/status`, { signal: controller.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((s) => s && !controller.signal.aborted && setStatus(s))
      .catch(() => { if (!controller.signal.aborted) setStatus(null) })
    void loadDashboards(authUser.username, controller.signal)
    return () => {
      controller.abort()
      if (saveTimer.current != null) window.clearTimeout(saveTimer.current)
    }
    // Account lifetime owns every save/load/refresh request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser])

  const loadDashboards = async (owner: string, signal: AbortSignal) => {
    const revision = boardRevision.current
    setSyncStatus('loading')
    try {
      const result = await fetchDashboards(owner, signal)
      const loaded = normalize(result.dashboards).filter((b) => b.id !== STG_LANDING_ID)
      if (signal.aborted) return
      const merged = new Map(loaded.map((d) => [d.id, d]))
      for (const [id, edited] of boardEdits.current) {
        if (edited <= revision) continue
        const current = dashboardsRef.current.find((d) => d.id === id)
        if (current) merged.set(id, current)
        else merged.delete(id)
      }
      replaceDashboards([...merged.values()])
      setSyncStatus(result.syncError ? 'error' : hasPendingDashboards(owner) ? 'saving' : 'saved')
    } catch {
      if (!signal.aborted) setSyncStatus('error')
    }
  }

  const handleLogout = async () => {
    if (loggingOut.current) return
    if (authUser && hasPendingDashboards(authUser.username)
      && !window.confirm('Some dashboard changes have not reached the server. Sign out and discard this browser backup?')) return
    loggingOut.current = true
    setLogoutError(null)
    try {
      await logout()
    } catch {
      setLogoutError('Could not sign out. Your session is still active. Please try again.')
      return
    } finally {
      loggingOut.current = false
    }
    abort.current?.abort()
    dashboardSession.current?.abort()
    if (saveTimer.current != null) window.clearTimeout(saveTimer.current)
    clearClientState()  // AUDIT W1 — purge supplier scope + cached board state on logout
    clearDashboardMemory()
    clearRunSpecCache()
    signedOutRef.current = true
    lastUsernameRef.current = null
    setAuthUser(null)
    replaceDashboards([])
    setTurns([])
    history.current = []
    setAskTargetId(null)
    setBusy(false)
    setNaming(null)
    clearReturnPath()
    navigate(DEFAULT_ROUTE, { replace: true })
  }

  const persistDashboardChanges = async () => {
    if (!authUser || !dashboardSession.current) return
    const signal = dashboardSession.current.signal
    setSyncStatus('saving')
    try {
      await syncDashboards(authUser.username, signal)
      if (!signal.aborted) setSyncStatus(hasPendingDashboards(authUser.username) ? 'saving' : 'saved')
    } catch {
      if (!signal.aborted) setSyncStatus('error')
    }
  }

  // Backup immediately; debounce only the network write. Closing the tab during
  // the debounce leaves an outbox that is retried on the next authenticated load.
  const scheduleSave = (dash: Dashboard) => {
    if (!authUser) return
    stageDashboard(dash, authUser.username)
    setSyncStatus('saving')
    if (saveTimer.current != null) window.clearTimeout(saveTimer.current)
    saveTimer.current = window.setTimeout(() => void persistDashboardChanges(), 500)
  }
  const commitDash = (updated: Dashboard) => {
    const stamped = { ...updated, updated_at: nowIso() }
    boardEdits.current.set(stamped.id, ++boardRevision.current)
    const prev = dashboardsRef.current
    replaceDashboards(prev.some((d) => d.id === stamped.id)
      ? prev.map((d) => d.id === stamped.id ? stamped : d) : [...prev, stamped])
    scheduleSave(stamped)
  }
  const updateBoard = (id: string, update: (board: Dashboard) => Dashboard) => {
    const current = dashboardsRef.current.find((d) => d.id === id)
    if (current) commitDash(update(current))
  }

  const restoreLegacyBoards = () => {
    const missing = normalize(legacyDashboardBackup()).filter((b) => b.id !== STG_LANDING_ID
      && !dashboardsRef.current.some((d) => d.id === b.id))
    missing.forEach(commitDash)
    clearLegacyDashboardBackup()
    setHasLegacyBoards(false)
  }

  // --- chat ---
  const patch = (id: string, fields: Partial<AssistantTurn>) =>
    setTurns((prev) =>
      prev.map((t) => (t.id === id && t.role === 'assistant' ? { ...t, ...fields } : t)),
    )

  const handleAsk = (q: string, context: HistoryTurn[] = history.current) => {
    const question = q.trim()
    if (!chatOn || !question || (abort.current && !abort.current.signal.aborted)) return
    abort.current?.abort()
    const controller = new AbortController()
    abort.current = controller

    const assistantId = uuid()
    const sentHistory = [...context]
    setTurns((prev) => [
      ...prev,
      { id: uuid(), role: 'user', text: question },
      { id: assistantId, role: 'assistant', status: 'thinking', steps: [], question, history: sentHistory },
    ])
    setValue('')
    setBusy(true)
    if (activeId) setAskTargetId(activeId)
    openMyDashboards() // the chat lives in the Mina dashboards workspace (chat sub-view, not a board)

    const isCurrent = () => abort.current === controller && !controller.signal.aborted
    const finish = (fields: Partial<AssistantTurn>) =>
      setTurns((prev) =>
        prev.map((t) =>
          t.id === assistantId && t.role === 'assistant' ? { ...t, ...fields } : t,
        ),
      )

    // Coalesce streamed deltas: buffer prose and apply at most once per frame, so
    // a burst of SSE chunks is a single React update — not one render per token.
    let pendingText = ''
    let rafId: number | null = null
    const flush = () => {
      rafId = null
      if (!pendingText) return
      const chunk = pendingText
      pendingText = ''
      setTurns((prev) =>
        prev.map((t) =>
          t.id === assistantId &&
          t.role === 'assistant' &&
          (t.status === 'thinking' || t.status === 'streaming')
            ? { ...t, status: 'streaming', answer: (t.answer ?? '') + chunk }
            : t,
        ),
      )
    }
    const scheduleFlush = () => {
      if (rafId == null) rafId = requestAnimationFrame(flush)
    }
    const stopFlush = () => {
      if (rafId != null) cancelAnimationFrame(rafId)
      rafId = null
      pendingText = ''
    }

    void ask(question, sentHistory, {
      signal: controller.signal,
      onAck: (a) => { if (isCurrent()) patch(assistantId, { ack: a.text }) },
      onStep: (s) => {
        if (!isCurrent()) return
        setTurns((prev) =>
          prev.map((t) =>
            t.id === assistantId && t.role === 'assistant'
              ? { ...t, steps: [...t.steps, s.label] }
              : t,
          ),
        )
      },
      // A chart is coming — reserve space + show a skeleton, but DON'T mount the
      // real chart yet. It is mounted once, at commit, so it never swaps/vanishes.
      onChart: () => { if (isCurrent()) patch(assistantId, { chartPending: true }) },
      // Stream the prose; `reset` clears a draft the model is recomposing.
      onDelta: (d) => {
        if (!isCurrent()) return
        if (d.reset) {
          stopFlush()
          patch(assistantId, { answer: '', status: 'streaming' })
          return
        }
        pendingText += d.text
        scheduleFlush()
      },
      onAnswer: (ans) => {
        if (!isCurrent()) return
        stopFlush() // committed answer is authoritative — drop any buffered draft
        finish({
          status: 'done',
          answer: ans.answer,
          caveat: ans.caveat,
          caveat_info: ans.caveat_info ?? null,
          chart: ans.chart,
          spec: ans.spec ?? null,
          chartPending: false,
          preliminary: ans.preliminary,
          pinnable: ans.pinnable ?? !!(ans.spec || ans.chart),
        })
        abort.current = null
        setBusy(false)
        const updated: HistoryTurn[] = [
          ...sentHistory,
          { role: 'user', content: question },
          { role: 'assistant', content: ans.answer },
        ]
        history.current = updated.slice(-8)
      },
      onError: (e) => {
        if (!isCurrent()) return
        // Drop any optimistically-streamed prose — an ungrounded draft must not persist.
        stopFlush()
        finish({ status: 'error', error: e.message, answer: undefined, chart: null, chartPending: false })
        abort.current = null
        setBusy(false)
      },
    })
  }

  // Reset the chat function to a clean slate (the docked ask-bar starts a new
  // question). Chat is a transient tool, not a persisted history in the nav.
  const newChat = () => {
    abort.current?.abort()
    abort.current = null
    setTurns([])
    setPinnedTurnIds(new Set())
    setValue('')
    setBusy(false)
    history.current = []
    setAskTargetId(null)
    openMyDashboards() // chat sub-view of the dashboard workspace
  }

  const stopQuestion = () => {
    // The stopped message follows the question's language like ask.ts's other messages (MP-OS-193).
    const signal = abort.current?.signal
    abort.current?.abort()
    abort.current = null
    setBusy(false)
    setTurns((prev) => prev.map((t) => t.role === 'assistant' && (t.status === 'thinking' || t.status === 'streaming')
      ? { ...t, status: 'error', error: stoppedMessage(signal, t.question, t.history), answer: undefined, chartPending: false } : t))
  }

  // --- pinning (explicit target) ---
  // Flow new cards left→right across the full 12-col width (3 per row), not
  // stacked in the left column — so the canvas fills out as you pin.
  const flowSlot = (n: number, h: number): CardLayout => ({ x: (n % 3) * 4, y: Math.floor(n / 3) * 8, w: 4, h })

  const makeCard = (turn: AssistantTurn, n: number): ChartCard => ({
    id: uuid(),
    type: 'chart',
    title: summarize(turn.question ?? ''),
    question: turn.question, // exact NL question → faithful re-grounding later
    history: turn.history,
    spec: turn.spec ?? null,
    last_contract: { answer: turn.answer ?? '', caveat: turn.caveat ?? null, caveat_info: turn.caveat_info ?? null,
      chart: turn.chart ?? null, preliminary: turn.preliminary },
    pinned_at: nowIso(),
    layout: flowSlot(n, 8),
  })

  // Pin the chart BEHIND an insight: fetch its contract and add it as a card to
  // the most-recent board (or a new "Insikter" board). Insights are measure
  // definitions, so this pins the live query — not a snapshot of text.
  const [pinnedInsightIds, setPinnedInsightIds] = useState<Set<string>>(new Set())
  // MP-OS-192: what the Overview share card shows, reported by LandingHero (which owns the card),
  // so the segment rows below hide only a % the card already states.
  const [shareCard, setShareCard] = useState<ShareCardFigure | null>(null)
  const pinInsight = async (ins: Insight) => {
    let contract: Answer
    try {
      contract = await runSpec(ins.spec, { signal: dashboardSession.current?.signal })
    } catch {
      setWorkspaceError('Could not fetch this insight. Try pinning it again in a moment.')
      return
    }
    if (dashboardSession.current?.signal.aborted) return
    const recent = [...dashboardsRef.current].sort((a, b) => (b.updated_at ?? '').localeCompare(a.updated_at ?? ''))[0]
    const board: Dashboard =
      recent ?? { id: uuid(), name: 'Insikter', created_at: nowIso(), updated_at: nowIso(), cards: [] }
    const card: ChartCard = {
      id: uuid(),
      type: 'chart',
      title: ins.title,
      spec: ins.spec,
      last_contract: contract,
      pinned_at: nowIso(),
      layout: flowSlot(board.cards.length, 8),
    }
    commitDash({ ...board, cards: [...board.cards, card] })
    setPinnedInsightIds((prev) => new Set(prev).add(ins.id))
  }

  const pinTo = (turn: AssistantTurn, target: string | 'new') => {
    if (turn.status !== 'done' || turn.pinnable === false) return
    // MP-OS-189 — a new board is named first; nothing exists until the dialog is confirmed.
    if (target === 'new') { setNaming({ turn }); return }
    const board = dashboardsRef.current.find((d) => d.id === target)
    if (!board) return
    const card = makeCard(turn, board.cards.length)
    commitDash({ ...board, cards: [...board.cards, card] })
    setPinnedTurnIds((prev) => new Set(prev).add(turn.id))
  }

  // --- dashboards ---
  const newDashboard = () => {
    setMobileNavOpen(false)
    setNaming({})
  }
  // The only place a board is created from the UI's "New dashboard" — after it has a name.
  const createNamedDashboard = (name: string, turn?: AssistantTurn) => {
    const d: Dashboard = { id: uuid(), name, created_at: nowIso(), updated_at: nowIso(), cards: [] }
    const pinnable = turn && turn.status === 'done' && turn.pinnable !== false
    commitDash(pinnable ? { ...d, cards: [makeCard(turn, 0)] } : d)
    if (pinnable) setPinnedTurnIds((prev) => new Set(prev).add(turn.id))
    openedBoard.current = d.id // just built from fresh results; nothing to re-run
    openBoard(d.id)
  }
  const selectDashboard = (id: string) => {
    openBoard(id)
    setMobileNavOpen(false)
  }
  const removeDashboard = (id: string) => {
    const board = dashboards.find((d) => d.id === id)
    const n = board?.cards.length ?? 0
    // A hand-built board is the highest-regret thing to lose — confirm when it holds work.
    if (n > 0 && !window.confirm(`Remove "${board?.name}"? ${n} card(s) will be lost and this can't be undone.`)) return
    boardEdits.current.set(id, ++boardRevision.current)
    replaceDashboards(dashboardsRef.current.filter((d) => d.id !== id))
    if (authUser) stageDashboardDeletion(id, authUser.username)
    void persistDashboardChanges()
    // Back to the chat sub-view; replace, so Back never lands on the removed board.
    if (activeId === id) openMyDashboards({ replace: true })
  }
  const selectSection = (id: SectionId) => {
    // Entering the dashboard workspace lands on the AI chat (the build tool), not a board.
    navigate({ section: id, boardId: null })
    setMobileNavOpen(false)
  }

  // --- card ops on the active dashboard ---
  const active = dashboards.find((d) => d.id === activeId) ?? null
  const activeLoaded = active !== null
  const renameBoard = (name: string) => active && commitDash({ ...active, name })
  const renameCard = (cardId: string, title: string) =>
    active &&
    commitDash({
      ...active,
      cards: active.cards.map((c) => (c.id === cardId && c.type === 'chart' ? { ...c, title } : c)),
    })
  const editText = (cardId: string, patchFields: { heading?: string; body?: string }) =>
    active &&
    commitDash({
      ...active,
      cards: active.cards.map((c) => (c.id === cardId && c.type === 'text' ? { ...c, ...patchFields } : c)),
    })
  const removeCard = (cardId: string) => {
    if (!active) return
    if (!window.confirm('Remove the card?')) return
    commitDash({ ...active, cards: active.cards.filter((c) => c.id !== cardId) })
  }
  const addTextCard = () => {
    if (!active) return
    const card: TextCard = {
      id: uuid(),
      type: 'text',
      heading: 'Heading',
      body: '',
      layout: flowSlot(active.cards.length, 4),
    }
    commitDash({ ...active, cards: [...active.cards, card] })
  }
  // MP-OS-52: meeting → action card (heading + checklist).
  const addActionCard = () => {
    if (!active) return
    const card: ActionCard = {
      id: uuid(),
      type: 'action',
      heading: 'Actions',
      items: [],
      layout: flowSlot(active.cards.length, 4),
    }
    commitDash({ ...active, cards: [...active.cards, card] })
  }
  const editAction = (cardId: string, patch: { heading?: string; items?: ActionItem[] }) =>
    active &&
    commitDash({
      ...active,
      cards: active.cards.map((c) => (c.id === cardId && c.type === 'action' ? { ...c, ...patch } : c)),
    })
  // MP-OS-53: starter TEMPLATES — scaffold an empty board with text/action cards the user
  // then fills (and pins charts into). Pure scaffolding, no chart-fetch. Self-service edit
  // (pin / add / drag / resize / delete / rename / multi-board) already exists.
  const applyTemplate = (key: 'meeting' | 'monthly') => {
    if (!active) return
    const txt = (heading: string): TextCard => ({ id: uuid(), type: 'text', heading, body: '' })
    const act = (heading: string): ActionCard => ({ id: uuid(), type: 'action', heading, items: [] })
    const cards: DashboardCard[] =
      key === 'meeting'
        ? [txt('Summary'), act('Actions')]
        : [txt('Top lists'), txt('Packages'), act('To follow up')]
    const based = active.cards.length
    const laid = cards.map((c, i) => ({ ...c, layout: flowSlot(based + i, 4) }))
    commitDash({ ...active, cards: [...active.cards, ...laid] })
  }
  const moveCard = (cardId: string, targetId: string) => {
    if (!active) return
    const card = active.cards.find((c) => c.id === cardId)
    const target = dashboards.find((d) => d.id === targetId)
    if (!card || !target) return
    commitDash({ ...active, cards: active.cards.filter((c) => c.id !== cardId) })
    commitDash({ ...target, cards: [...target.cards, { ...card, layout: undefined }] })
  }
  const applyLayout = (map: Record<string, CardLayout>) => {
    if (!active) return
    const changed = active.cards.some((c) => {
      const l = map[c.id]
      return l && (!c.layout || c.layout.x !== l.x || c.layout.y !== l.y || c.layout.w !== l.w || c.layout.h !== l.h)
    })
    if (!changed) return
    commitDash({ ...active, cards: active.cards.map((c) => (map[c.id] ? { ...c, layout: map[c.id] } : c)) })
  }
  const beginRefresh = (cardId: string) => {
    if (refreshInFlight.current.has(cardId)) return false
    refreshInFlight.current.add(cardId)
    setRefreshingIds((prev) => new Set(prev).add(cardId))
    setCardErrors((prev) => { const next = { ...prev }; delete next[cardId]; return next })
    return true
  }
  const endRefresh = (cardId: string) => {
    refreshInFlight.current.delete(cardId)
    setRefreshingIds((prev) => { const next = new Set(prev); next.delete(cardId); return next })
  }
  const refreshOne = async (boardId: string, cardId: string, fresh = true) => {
    const card = dashboardsRef.current.find((d) => d.id === boardId)?.cards.find((c) => c.id === cardId)
    const signal = dashboardSession.current?.signal
    if (!card || card.type !== 'chart' || !card.spec || signal?.aborted || !beginRefresh(cardId)) return
    const specKey = JSON.stringify(card.spec)
    try {
      const contract = await runSpec(card.spec, { fresh, signal })
      if (signal?.aborted) return
      setPreviewRefused(cardId, false)
      updateBoard(boardId, (current) => ({ ...current, cards: current.cards.map((c) => {
        if (!(c.id === cardId && c.type === 'chart' && JSON.stringify(c.spec) === specKey)) return c
        return { ...c, out_of_scope: undefined, last_contract: contract, pinned_at: contract.stale ? c.pinned_at : nowIso() }
      }) }))
    } catch (err) {
      if (signal?.aborted) return
      // MPD-84 N1: refused under the supplier scope → the card shows that state and the stored
      // snapshot is replaced (and saved), so its figures never show again, online or offline.
      // MPD-91 L1: only a real supplier session saves it. An admin previewing a supplier is
      // refreshing the admin's OWN cards, so the refusal is view state and the stored card stays.
      if (err instanceof OutOfScopeError && authUser?.role !== 'supplier') {
        setPreviewRefused(cardId, true)
        return
      }
      if (err instanceof OutOfScopeError) {
        updateBoard(boardId, (current) => ({ ...current, cards: current.cards.map((c) =>
          c.id === cardId && c.type === 'chart' && JSON.stringify(c.spec) === specKey
            ? { ...c, out_of_scope: true, last_contract: OUT_OF_SCOPE_CONTRACT } : c) }))
        return
      }
      setCardErrors((prev) => ({ ...prev, [cardId]: 'Could not update. Showing the last successful result.' }))
    } finally {
      endRefresh(cardId)
    }
  }
  const refreshCard = (cardId: string) => activeId && refreshOne(activeId, cardId)

  // Re-ground a stale pin: re-ask its original question through the (now-correct)
  // chat and replace the stored spec + contract. Fixes cards pinned by an older,
  // buggier orchestrator (which a plain refresh can't — it re-runs the wrong spec).
  const regroundCard = async (cardId: string) => {
    if (!chatOn || !activeId) return
    const boardId = activeId
    const card = dashboardsRef.current.find((d) => d.id === boardId)?.cards.find((c) => c.id === cardId)
    if (!card || card.type !== 'chart') return
    const question = (card.question ?? card.title).trim()
    if (!question) return
    const signal = dashboardSession.current?.signal
    if (signal?.aborted || !beginRefresh(cardId)) return
    try {
      await ask(question, card.history ?? [], {
        signal,
        onAnswer: (ans) => {
          if (signal?.aborted) return
          if (ans.pinnable === false) {
            setCardErrors((prev) => ({ ...prev, [cardId]: 'The AI could not rebuild this view. Showing the last successful result.' }))
            return
          }
          setPreviewRefused(cardId, false)
          updateBoard(boardId, (current) => ({ ...current, cards: current.cards.map((c) =>
            c.id === cardId && c.type === 'chart'
              ? { ...c, spec: ans.spec ?? null, last_contract: ans, pinned_at: nowIso(), out_of_scope: undefined } : c) }))
        },
        onError: (err) => {
          if (!signal?.aborted) setCardErrors((prev) => ({ ...prev, [cardId]: `${err.message} Showing the last successful result.` }))
        },
      })
    } finally { endRefresh(cardId) }
  }

  // Batch refresh a board's chart cards (no LLM — each is a /api/run-spec call).
  // Maps results back onto the CURRENT board so concurrent layout edits survive.
  const refreshBoard = async (boardId: string, onlyPreliminary = false) => {
    const board = dashboardsRef.current.find((d) => d.id === boardId)
    if (!board) return
    const targets = board.cards.filter(
      (c): c is ChartCard =>
        c.type === 'chart' && !!c.spec && (!onlyPreliminary || !!c.last_contract.preliminary),
    )
    if (targets.length === 0) return
    // Bound upstream work when opening a large dashboard.
    for (let i = 0; i < targets.length; i += 3) {
      if (dashboardSession.current?.signal.aborted) break
      await Promise.all(targets.slice(i, i + 3).map((c) => refreshOne(boardId, c.id, onlyPreliminary)))
    }
  }

  // Re-run saved queries when a board is opened → live numbers. Keyed on the route, so a link
  // or Back/Forward to a board refreshes it exactly like a click, once the board has loaded.
  useEffect(() => {
    if (!activeId) { openedBoard.current = null; return }
    if (!activeLoaded || openedBoard.current === activeId) return
    openedBoard.current = activeId
    void refreshBoard(activeId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, activeLoaded])

  // While viewing a board with open-period (preliminary) cards, refresh just
  // those on a gentle interval so live windows keep ticking.
  useEffect(() => {
    if (activeSection !== 'mina' || !activeId) return
    const board = dashboards.find((d) => d.id === activeId)
    const hasPreliminary = !!board?.cards.some(
      (c) => c.type === 'chart' && c.last_contract.preliminary,
    )
    if (!hasPreliminary) return
    const timer = window.setInterval(() => void refreshBoard(activeId, true), 60_000)
    return () => window.clearInterval(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection, activeId, dashboards])

  if (authUser === undefined) {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: 'var(--brand-dark)', color: '#fff' }}>
        {authError ? <div style={{ textAlign: 'center', padding: 24 }}>
          <p role="alert">Could not verify your session. Check your connection and try again.</p>
          <button onClick={() => setAuthAttempt((n) => n + 1)}>Retry connection</button>
        </div> : 'Loading…'}
      </div>
    )
  }
  if (!authUser) {
    return (
      <Login
        onAuthed={(u) => {
          // A full reload re-mounts with the session cookie reliably in the jar — the immediate
          // post-login request can miss the just-set cookie, so data came up empty until a manual
          // reload (Olof). For a supplier, persist its locked scope first → a SINGLE reload.
          if (u.role === 'supplier' && u.manufacturer) setSupplier(u.manufacturer, u.manufacturer_label)
          else window.location.reload()
        }}
      />
    )
  }
  if (authUser.must_change) {
    // Admin-issued temp password → force the seat to set its own before the dashboard mounts. A
    // reload after success re-reads the refreshed (must_change=false) session cookie, same pattern
    // as login, so the tab lands cleanly on the dashboard.
    return <ChangePassword user={authUser} onDone={() => window.location.reload()} />
  }

  // A supplier seat following a link to an internal page: the UI says no (the API already
  // does). Hiding the nav entry alone would not be authorization.
  const denied = (activeSection === 'business' && (authUser.role !== 'admin' || previewingSupplier()))
    || (activeSection === 'admin' && authUser.role !== 'admin')

  return (
    <div className={`app ${collapsed ? 'sb-collapsed' : ''} ${mobileNavOpen ? 'mobile-nav-open' : ''}`}>
      <div ref={mobileNavRef} style={{ display: 'contents' }}>
        <Sidebar
          user={authUser}
          onLogout={handleLogout}
          dashboards={dashboards}
          activeId={activeId}
          collapsed={collapsed}
          dropActive={draggingTurn !== null}
          activeSection={activeSection}
          chatEnabled={chatOn}
          onToggle={() => setCollapsed((c) => !c)}
          onSelectSection={selectSection}
          onSelect={selectDashboard}
          onNew={newDashboard}
          onDelete={removeDashboard}
          onDropToBoard={(boardId) => {
            if (draggingTurn) pinTo(draggingTurn, boardId)
            setDraggingTurn(null)
          }}
        />
      </div>

      <button
        className="mobile-nav-backdrop"
        aria-label="Close menu"
        onClick={() => setMobileNavOpen(false)}
      />

      <main className="main">
        {logoutError && <div className="section-pad" role="alert">{logoutError}</div>}
        <div className="topbar-right">
          <button
            ref={mobileNavToggleRef}
            className="mobile-nav-toggle"
            aria-label="Open menu"
            aria-expanded={mobileNavOpen}
            onClick={() => setMobileNavOpen((v) => !v)}
          >
            <Menu size={18} />
          </button>
          {authUser.role === 'admin' ? (
            // In "View as supplier" the internal page is denied, so the topbar stays the preview's
            // picker, as on every other route (MPD-46 #5).
            activeSection === 'business' && !previewingSupplier() ? <span>All suppliers</span> : <SupplierPicker />
          ) : (
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--brand-dark)', padding: '6px 10px' }}>
              {ACTIVE_SUPPLIER.label}
              <InfoTip label="About your supplier view" size="small">You only see your own supplier data.</InfoTip>
            </span>
          )}
          <span className={`mode-badge ${status?.mode ?? ''}`}>
            {status?.mode === 'live' ? 'Live data' : status?.mode === 'canned' ? 'Demo mode'
              : status?.mode === 'unavailable' ? 'Data connection unavailable' : '…'}
          </span>
          {(status?.mode === 'live' || status?.mode === 'canned') && (
            <InfoTip label="About the data source" size="small">
              {status.mode === 'live'
                ? `Live ${DATA_SOURCE_NAME} (not a demo). Sales figures update daily${chatOn ? '; the chat answers in real time' : ''}.`
                : 'Demo mode — sample data, not live figures.'}
            </InfoTip>
          )}
        </div>

        <DataFreshnessNote periods={periods} lastIngestedAt={status?.last_ingested_at} />

        {/* Month-based sections retain their period selector. Sales Trends uses its own
            shared Range and Group by controls; boards carry their saved date specifications. */}
        {periods.length > 0 && period && !denied && activeSection !== 'mina' && activeSection !== 'admin' && activeSection !== 'trends' && (
          <div className="section-pad global-period">
            <MonthPicker periods={periods} value={period} onChange={choosePeriod} />
          </div>
        )}

        {denied && (
          <div className="section-body"><div className="section-pad route-notice" role="alert">
            <h1 className="section-h1">No access</h1>
            <p>This page is only available to MinPrilla staff.</p>
            <button type="button" className="explore-trends" onClick={() => navigate(DEFAULT_ROUTE, { replace: true })}>Go to Overview</button>
          </div></div>
        )}
        {activeSection === 'business' && authUser.role === 'admin' && !previewingSupplier() && (
          <div className="section-body"><div className="section-pad">
            {period ? <BusinessMetrics period={period} periods={periods} role={authUser.role} /> :
              <ReportingPeriodsState loading={periodsLoading} onRetry={() => setPeriodsReload(n => n + 1)} />}
          </div></div>
        )}
        {/* Keep one controller mounted between these views so trend selections survive navigation. */}
        {(activeSection === 'oversikt' || activeSection === 'trends') && (
          <div className="section-body">
            {/* MP-OS-191 D: the Overview's two pads read as one column, without the 40 + 28 px
                section padding stacked between the Reconciliation card and the segment cards. */}
            <div className={activeSection === 'oversikt' ? 'section-pad overview-main' : 'section-pad'}>
              <h1 className="section-h1">{activeSection === 'trends' ? 'Sales Trends' : 'Overview'}</h1>
              {period ? <LandingHero
                view={activeSection === 'trends' ? 'trends' : 'overview'}
                onPinInsight={pinInsight}
                pinnedInsightIds={pinnedInsightIds}
                period={period}
                periods={periods}
                username={authUser?.username}
                onShareCard={setShareCard}
              /> : <ReportingPeriodsState loading={periodsLoading} onRetry={() => setPeriodsReload((n) => n + 1)} />}
            </div>
            {/* Segment split (Snus/Vape/Other in CU) belongs on the Overview, not Packages —
                Olof 4/9: "den hör ju inte hit [Packages]... undrar om vi skulle kunna lägga den på overview". */}
            {period && activeSection === 'oversikt' && (
              <div className="section-pad overview-segments">
                <SegmentView period={period} shareCard={shareCard} />
                <button className="explore-trends" onClick={() => selectSection('trends')}>Explore Sales Trends →</button>
              </div>
            )}
          </div>
        )}
        {activeSection === 'forsaljning' && (
          <div className="section-body">
            <div className="section-pad">
              <h1 className="section-h1">Top lists</h1>
              {/* MP-OS-103 — per-area blocks (meeting 25/8): one block per product area the
                  supplier has (Snus/Vape/Smoking/Övrigt), each with its own article + brand
                  leaderboards and unit. Replaces the single filterable list. */}
              {/* dataEnd anchors the Week default on the latest reporting period's own through
                  date (same pattern as LandingHero's dataEnd), never on `period` — the globally
                  shared month, which by design can lag behind the latest completed week
                  (MP-OS-188 bug #3). */}
              {period ? <AreaTopLists period={period} historyFrom={periods[periods.length - 1]?.from}
                                      dataEnd={periods[0]?.through ?? periods[0]?.to ?? period.to} />
                : <ReportingPeriodsState loading={periodsLoading} onRetry={() => setPeriodsReload((n) => n + 1)} />}
            </div>
          </div>
        )}
        {activeSection === 'marknad' && (
          <div className="section-body">
            <div className="section-pad">
              <h1 className="section-h1">Packages</h1>
              {/* B4 / meeting 25/8 — per-area pack-size blocks (Snus in CU, vape/smoking in
                  units), each with brand + SKU drill. */}
              {period ? <AreaPackages period={period} />
                : <ReportingPeriodsState loading={periodsLoading} onRetry={() => setPeriodsReload((n) => n + 1)} />}
              {/* The trailing market/share cards (CatalogSection id="marknad") were removed from
                  this view for now — Olof 31/8: "plocka bort det som är under här så länge". */}
            </div>
          </div>
        )}
        {USER_MGMT_UI_ENABLED && activeSection === 'admin' && authUser.role === 'admin' && (
          <div className="section-body">
            <AdminUsers />
          </div>
        )}

        {/* Mina dashboards — the chat (build tool) when no board is open, else the
            board grid. The ask-bar is docked here only: chat is a feature of this
            workspace, not a global mode. */}
        {activeSection === 'mina' && (
          <>
            {/* The "saved" message is dashboard status, not a description of the chat:
                keep it out of view while the chat is shown with nothing to report, so
                it doesn't read as the chat's own subtitle (MP-OS-191). Loading/saving/
                error states and the legacy-recovery offer stay visible either way —
                they're actionable regardless of which sub-view is open. */}
            {(active || syncStatus !== 'saved' || hasLegacyBoards) && (
              <div className={`dashboard-sync ${syncStatus}`} role="status">
                <span>{syncStatus === 'loading' ? 'Loading saved dashboards…'
                  : syncStatus === 'saving' ? 'Saving changes…'
                    : syncStatus === 'error' ? 'Could not sync dashboards. Your browser backup is kept; retry to save it to your account.'
                      : 'Dashboards saved to your account'}</span>
                {syncStatus === 'error' && <button type="button" onClick={() => {
                  if (authUser && dashboardSession.current) void loadDashboards(authUser.username, dashboardSession.current.signal)
                }}>Retry sync</button>}
                {hasLegacyBoards && <button type="button" onClick={restoreLegacyBoards}>Recover dashboards from this browser</button>}
              </div>
            )}
            {workspaceError && <p className="workspace-error" role="alert">{workspaceError}
              <button type="button" onClick={() => setWorkspaceError(null)}>Dismiss</button>
            </p>}
            {active ? (
              <div className="mina-body">
                {/* MPD-76 F7: keyed by board, so switching boards remounts the view and its
                    useDownload aborts an export still running for the previous board. */}
                <DashboardView
                  key={active.id}
                  dashboard={withPreviewOutOfScope(active, previewOutOfScope)}
                  dashboards={dashboards}
                  refreshingIds={refreshingIds}
                  cardErrors={cardErrors}
                  onLayoutChange={applyLayout}
                  onRenameCard={renameCard}
                  onEditText={editText}
                  onRefreshCard={refreshCard}
                  onRegroundCard={chatOn ? regroundCard : undefined}
                  onRemoveCard={removeCard}
                  onMoveCard={moveCard}
                  onRenameBoard={renameBoard}
                  onAddText={addTextCard}
                  onAddAction={addActionCard}
                  onEditAction={editAction}
                  onApplyTemplate={applyTemplate}
                />
              </div>
            ) : activeId ? (
              // A board link that isn't (yet) in this account's boards: still loading, or not
              // this account's — the server only ever returns the signed-in user's boards.
              <div className="section-pad route-notice" role={syncStatus === 'loading' ? 'status' : 'alert'}>
                {syncStatus === 'loading' ? <p>Loading dashboard…</p> : <>
                  <h1 className="section-h1">Dashboard not found</h1>
                  <p>It may have been removed, or it belongs to another account.</p>
                  <button type="button" className="explore-trends" onClick={() => openMyDashboards({ replace: true })}>Go to My dashboards</button>
                </>}
              </div>
            ) : !chatOn ? (
              <DashboardIndex dashboards={dashboards} onOpen={selectDashboard} onNew={newDashboard} />
            ) : (
              <ChatView
                internal={authUser.role === 'admin'}
                turns={turns}
                onAsk={handleAsk}
                onRetry={(turn) => handleAsk(turn.question ?? '', turn.history ?? [])}
                onNewChat={newChat}
                dashboards={dashboards}
                pinnedTurnIds={pinnedTurnIds}
                onPinTo={pinTo}
                onPinDragStart={(t) => setDraggingTurn(t)}
                onPinDragEnd={() => setDraggingTurn(null)}
                preferredDashboardId={askTargetId}
                onOpenDashboard={selectDashboard}
              />
            )}
            {chatOn && <AskBar value={value} setValue={setValue} busy={busy} onAsk={handleAsk} onStop={stopQuestion} dashboardName={active?.name} />}
          </>
        )}
      </main>
      {naming && (
        <NameDialog
          title={naming.turn ? 'Pin to a new dashboard' : 'New dashboard'}
          initialName={`Dashboard ${dashboards.length + 1}`}
          confirmLabel="Create"
          onCancel={() => setNaming(null)}
          onConfirm={(name) => {
            const turn = naming.turn
            setNaming(null)
            createNamedDashboard(name, turn)
          }}
        />
      )}
    </div>
  )
}
